import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { Fixture } from '@cds/betting-offer';
import { FixtureForm } from '@cds/betting-offer/add-ons';
import { Coupon as CdsCoupon, CouponPage, CouponSubType } from '@cds/betting-offer/domain-specific/coupons';
import { FixtureFormMode, SortByCriteria } from '@cds/query-objects';
import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { ClientCachingConfig, LayoutNavigationConfig, PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { ItemType, LeagueItem, children } from '@frontend/sports/common/core/data-access/sport-model';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { GridSorting } from '@frontend/sports/grid/core/feature/model';
import { ColumnLayoutService } from '@frontend/sports/layout/feature';
import { CompetitionRoute, TopNavigationVersion } from '@frontend/sports/navigation/core/feature/models';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { isArray, toInteger, toNumber } from 'lodash-es';
import { Observable, iif, map, of, switchMap, tap } from 'rxjs';

import { BetBuilderConfigService } from '../../bet-builder/services/bet-builder-config.service';
import { CalendarService } from '../../calendar/calendar.service';
import { CouponApi } from '../../cds/coupon-api.service';
import { SportsCacheService } from '../../client-caching/sports-cache.service';
import { CompetitionRouteService } from '../../competition-list/competition-route.service';
import { MasterDetailComponent, MasterDetailDependencyService, MasterDetailState } from '../../dependency/master-detail-dependency.service';
import { StatisticsConfigService } from '../../statistics/statistics-config.service';
import { SortingStateService } from '../sport/services/sorting-state.service';
import { BetBuilderDetail } from './bet-builder/bet-builder-detail.model';
import { BetBuilderCouponService } from './bet-builder/betbuilder-coupon.service';
import { CouponListComponent } from './coupon-list.component';
import { Coupon, CouponDetail, CouponList, CouponType } from './coupon.model';

@Injectable({ providedIn: 'root' })
export class CouponService extends MasterDetailDependencyService<CouponList, CouponDetail> {
    private cache: ExpiringCache;

    constructor(
        private sitecore: Sitecore,
        private routerEvents: RouterEventsService,
        private sportCacheService: SportsCacheService,
        private sortingStore: SortingStateService,
        private urlConfig: PrettyUrlsConfig,
        private device: DeviceService,
        private couponApiService: CouponApi,
        private betBuilderService: BetBuilderCouponService,
        private betBuilderConfigService: BetBuilderConfigService,
        private nativeAppService: NativeAppService,
        layoutService: ColumnLayoutService,
        clientCachingConfig: ClientCachingConfig,
        private calendar: CalendarService,
        private layoutNavConfig: LayoutNavigationConfig,
        private competitionRoute: CompetitionRouteService,
        private statisticsConfigService: StatisticsConfigService,
    ) {
        super(layoutService);

        this.cache = new ExpiringCache(clientCachingConfig.couponListTimeout);

        this.routerEvents.currentActivationEnd.pipe(takeUntilDestroyed()).subscribe((route) => {
            if (route && route.snapshot.data.tag !== RouteTag.Coupons && route.snapshot.data.tag !== RouteTag.CouponDetails) {
                this.deregister();
            }
        });
    }

    getRouteParams: undefined;

    getData(route: ActivatedRouteSnapshot, detail?: CouponDetail): Observable<CouponList | undefined> {
        const routeParams = this.competitionRoute.params();
        const { sport, competition, virtualCompetition } = this.getParam(route, routeParams);

        if (!sport) {
            throw new Error('Sport should be defined');
        }

        const isBetbuilderEnabled = this.betBuilderConfigService.isBetBuilderEnabled(sport);

        const betBuilder = {
            id: CouponType.BetBuilder,
            title: this.sitecore.betBuilder.messages?.BetBuilderEventsTitle ?? '',
            description: this.sitecore.betBuilder.messages?.BetBuilderEventsTitle ?? '',
            type: CouponType.BetBuilder,
            isFormCoupon: false,
            showMarketSwitcher: false,
        };

        const couponSubType = this.nativeAppService.isTerminal ? CouponSubType.Retail : CouponSubType.Regular;
        const source = this.couponApiService.getCoupons(sport, couponSubType, competition, virtualCompetition).pipe(
            map((coupons) => {
                const mapped = !coupons?.length ? [] : coupons.map((coupon) => this.getCouponModel(coupon));
                const target: number = detail ? this.getCouponId(detail) : toNumber(this.getDetailTarget(mapped)?.id);

                if (isBetbuilderEnabled) {
                    if (this.layoutNavConfig.topNavigationVersion === TopNavigationVersion.V3) {
                        mapped.push(betBuilder);
                    } else if (this.layoutNavConfig.topNavigationVersion === TopNavigationVersion.V2 && coupons?.length) {
                        mapped.unshift(betBuilder);
                    }
                }

                return {
                    coupons: mapped.map((coupon) => ({ ...coupon, active: coupon.id === target })),
                };
            }),
            switchMap((couponList) =>
                iif(() => couponList.coupons.length > 0, this.sportCacheService.getSport(sport), of(undefined)).pipe(
                    map((sportItem) => {
                        if (!sportItem) {
                            return;
                        }

                        const competitionContext = !!(competition || virtualCompetition);
                        const countCompetition = children(sportItem, ItemType.Competition).find((c) => c.id === (competition || virtualCompetition));
                        const shouldRemoveBetbuilder = !!(countCompetition && (!countCompetition.meta.betBuilder || countCompetition.isVirtual));

                        if ((!sportItem.meta.betBuilder && !competitionContext) || (competitionContext && shouldRemoveBetbuilder)) {
                            const betBuilderIndex = couponList.coupons.findIndex((c) => c.id === CouponType.BetBuilder);
                            if (betBuilderIndex !== -1) {
                                couponList.coupons.splice(betBuilderIndex, 1);
                            }
                        }

                        return { ...couponList, sport: { id: sport, name: sportItem.name }, counts: sportItem } as CouponList;
                    }),
                ),
            ),
        );

        const casheSuff = competition ? `-${competition}` : virtualCompetition ? `-v-${virtualCompetition}` : '';

        return this.cache.getOrCreate(`coupons-${sport}${casheSuff}`, source).pipe(
            tap((result) => {
                if (result?.coupons.length) {
                    let target: number | undefined;

                    if (detail) {
                        target = this.getCouponId(detail);
                    }

                    result.coupons.forEach((coupon) => (coupon.active = coupon.id === target));
                }
            }),
        );
    }

    getPreResolvedData(route: ActivatedRouteSnapshot): Observable<CouponList | undefined> {
        return of(undefined);
    }

    override updateState(state: MasterDetailState<CouponList, CouponDetail>): void {
        if (!state.master || !state.detail) {
            return;
        }

        state.master.coupons.forEach((coupon) => (coupon.active = coupon.id === this.getCouponId(state.detail!)));
    }

    override shouldUpdateDetailCache(detail: CouponDetail, route: ActivatedRouteSnapshot): boolean {
        const { couponId } = this.getParam(route);

        return ('id' in detail && detail.id !== couponId) || (couponId === CouponType.BetBuilder && !('fixture' in detail));
    }

    getDetailData(route: ActivatedRouteSnapshot, master?: CouponList | undefined): Observable<CouponDetail | undefined> {
        const routeParams = this.competitionRoute.params();
        const { couponId, sport, competition, virtualCompetition } = this.getParam(route, routeParams);
        const fallback = this.getDetailFallback(master);

        const target = fallback ? (fallback.id as number) : couponId;
        if (!target) {
            return of(undefined);
        }

        if (target === CouponType.BetBuilder) {
            if (virtualCompetition) {
                return of(undefined);
            }

            return this.betBuilderService.getDetailCoupon(sport, competition);
        }

        const interval = this.calendar.getTimeInterval(route.params.context);

        return this.getData(route).pipe(
            switchMap((couponList) => {
                const coupon: Coupon | undefined = couponList?.coupons.find((each) => each.id === target);
                if (!coupon) {
                    return of(undefined);
                }
                coupon.active = true;
                if (coupon.type === CouponType.FormCoupon) {
                    return this.getDetailCoupon(target, coupon.type, sport, FixtureFormMode.Overall, interval); // coupon-detail
                }

                let virtualCompetitionData: LeagueItem | undefined;
                if (virtualCompetition) {
                    const competitions = couponList?.counts ? children([couponList?.counts], ItemType.Competition) : [];
                    virtualCompetitionData = competitions.find((c) => c.id === virtualCompetition && c.isVirtual);
                }

                return this.getDetailCoupon(target, coupon.type, sport, undefined, interval, competition, virtualCompetitionData);
            }),
        );
    }

    override getPreResolvedDetailData(route: ActivatedRouteSnapshot): Observable<CouponDetail | undefined> {
        return of(undefined);
    }

    getComponents(): MasterDetailComponent<CouponList>[] {
        return [{ component: CouponListComponent, map: (master?: CouponList) => ({ list: master }) }];
    }

    getDetailFallback(overview?: CouponList): UrlParam | undefined {
        if (!overview?.coupons.length) {
            return;
        }

        const target = this.getDetailTarget(overview.coupons);

        if (target) {
            return { id: target.id, name: target.title };
        }

        return;
    }

    getCoupon(
        sportIds: string,
        couponId: number,
        couponsubtype?: CouponSubType,
        skip: number = 0,
        take: number = 10,
        sortBy: SortByCriteria = SortByCriteria.StartDate,
        form?: FixtureFormMode,
        interval?: { from?: string; to?: string },
        competition?: number,
        virtualCompetition?: number,
    ): Observable<CouponPage | undefined> {
        const competitionIds = competition ? `${competition}` : undefined;
        const virtualCompetitionIds = virtualCompetition ? `${virtualCompetition}` : undefined;
        const statisticsModes = this.statisticsConfigService.getStatisticsModesBySportId(toInteger(sportIds));
        const coupon = this.couponApiService.getCoupon(
            sportIds,
            couponId,
            skip,
            take,
            sortBy,
            statisticsModes,
            form,
            interval,
            couponsubtype,
            competitionIds,
            virtualCompetitionIds,
        );

        if (coupon && form) {
            return this.adaptFixtureFormModel(coupon as Observable<CouponPage>, form);
        }

        return coupon;
    }

    protected override hasDependency(route: ActivatedRouteSnapshot, master: CouponList | undefined): boolean {
        const param = this.getParam(route);

        if (this.device.isRobot && !param.couponId) {
            return false;
        }

        return super.hasDependency(route, master);
    }

    private adaptFixtureFormModel(couponPage: Observable<CouponPage>, formMode?: FixtureFormMode): Observable<CouponPage | undefined> {
        const filterForm = (fixtureForm: FixtureForm, filter: (keyof FixtureForm)[]) => {
            for (const key in fixtureForm) {
                const typedKey = key as keyof FixtureForm;
                if (!filter.includes(key as keyof FixtureForm)) {
                    //TODO any index issue, please investigate if below line is needed
                    //@ts-ignore below line doesn't make sense, but kept it to ensure no changes are happening to the behavior
                    fixtureForm[key] = undefined;
                }
                if (fixtureForm[typedKey]) {
                    fixtureForm[typedKey]!.outcomes = fixtureForm[typedKey]!.outcomes.reverse();
                }
            }
        };

        return couponPage.pipe(
            map((coupon) =>
                coupon.fixturePage.fixtures.reduce((result, fixture) => {
                    if (fixture.addons && fixture.addons.fixtureStatistics?.fixtureForm) {
                        const selectedForm: { [mode in FixtureFormMode]: (keyof FixtureForm)[] } = {
                            [FixtureFormMode.All]: ['overallAwayTeamForm', 'overallHomeTeamForm'],
                            [FixtureFormMode.Overall]: ['overallAwayTeamForm', 'overallHomeTeamForm'],
                            [FixtureFormMode.Home]: ['homeTeamHomeForm'],
                            [FixtureFormMode.Away]: ['awayTeamAwayForm'],
                            [FixtureFormMode.None]: [],
                        };
                        filterForm(fixture.addons.fixtureStatistics?.fixtureForm, selectedForm[formMode || FixtureFormMode.Overall]);
                    }

                    return result;
                }, coupon),
            ),
        );
    }

    private getCouponId(detail: CouponDetail | BetBuilderDetail): number {
        return detail.id ?? detail.type;
    }

    protected getDetailCoupon(
        id: number,
        type: CouponType,
        sport?: number,
        formType?: FixtureFormMode,
        interval?: { from?: string; to?: string },
        competition?: number,
        virtualCompetitionData?: LeagueItem,
    ): Observable<CouponDetail | undefined> {
        if (!sport) {
            return of(undefined);
        }

        const sorting = this.mapSorting(sport, type, formType);
        const criteria = this.getCriteria(sorting, type);
        const couponSubType = this.nativeAppService.isTerminal ? CouponSubType.Retail : CouponSubType.Regular;

        return this.getCoupon(sport.toString(), id, couponSubType, 0, 30, criteria, formType, interval, competition, virtualCompetitionData?.id).pipe(
            map((current) => {
                if (!current?.coupon) {
                    return;
                }
                let sportItem: SportModel;
                if (
                    !current.fixturePage.fixtures.length ||
                    (current.fixturePage.fixtures.length > 0 && current.fixturePage.fixtures[0].sport.id !== sport)
                ) {
                    sportItem = {
                        id: sport,
                        name: '',
                        virtual: false,
                    };
                } else {
                    sportItem = this.getSportModel(current.fixturePage.fixtures[0]);
                }

                return {
                    id: current.coupon.id,
                    title: current.coupon.title,
                    description: current.coupon.shortDescription,
                    gridGroups: current.coupon.gridGroups,
                    sport: sportItem,
                    page: current.fixturePage,
                    type,
                    sorting,
                    bgColor: current.coupon.backgroundColor,
                    textColor: current.coupon.titleTextColor,
                    detailsPageImageUrl: current.coupon.detailsPageBackgroundImage,
                    landingPageImageUrl: current.coupon.landingPageBackgroundImage,
                    order: current.coupon.order,
                    isFormCoupon: current.coupon.isFormCoupon,
                    couponType: current.coupon.contentType,
                    offerSelection: current.coupon.offerSelection,
                    showMarketSwitcher: current.coupon.showMarketSwitcher,
                    competition,
                    virtualCompetitionData,
                    sortByPrice: current.coupon.sortByPrice,
                } as CouponDetail;
            }),
        );
    }

    private getCriteria(sorting: GridSorting, type: CouponType): SortByCriteria {
        let criteria = SortByCriteria.Tags;

        if (sorting === GridSorting.Time) {
            criteria = SortByCriteria.StartDate;
        }

        if (type === CouponType.FormCoupon) {
            criteria = SortByCriteria.FixtureForm;
        }

        return criteria;
    }

    private getDetailTarget(list: Coupon[]): Coupon | undefined {
        return list.filter((coupon) => coupon.type !== CouponType.BetBuilder).shift();
    }

    private getCouponModel(coupon: CdsCoupon): Coupon {
        return {
            id: coupon.id,
            title: coupon.title,
            description: coupon.shortDescription,
            type: this.mapCouponType(coupon),
            bgColor: coupon.backgroundColor,
            textColor: coupon.titleTextColor,
            detailsPageImageUrl: coupon.detailsPageBackgroundImage,
            landingPageImageUrl: coupon.landingPageBackgroundImage,
            order: coupon.order,
            isFormCoupon: coupon.isFormCoupon,
            couponType: coupon.contentType,
            offerSelection: coupon.offerSelection,
            showMarketSwitcher: coupon.showMarketSwitcher,
            sortByPrice: coupon.sortByPrice,
            couponTag: coupon.couponTag ?? 'Other',
        };
    }

    private getParam(
        route: ActivatedRouteSnapshot,
        params?: CompetitionRoute,
    ): { sport?: number; couponId?: number; competition?: number; virtualCompetition?: number } {
        const getInteger = (value?: string) => toInteger(value) || undefined;

        let routeLeague: number | undefined;
        if (params?.league) {
            routeLeague = isArray(params.league) ? params.league[0] : params.league;
        }

        return {
            sport: getInteger(route.params.sport),
            couponId:
                route.routeConfig && route.routeConfig.path === this.urlConfig.translations.betBuilder
                    ? CouponType.BetBuilder
                    : getInteger(route.params.coupon),
            competition: !params?.isVirtual ? routeLeague : undefined,
            virtualCompetition: params?.isVirtual ? routeLeague : undefined,
        };
    }

    protected getSportModel(fixture: Fixture): SportModel {
        return {
            id: fixture.sport.id,
            name: fixture.sport.name.value,
            virtual: fixture.isVirtual,
        };
    }

    private mapSorting(sport: number, type: CouponType, fixtureFormType: string | undefined): GridSorting {
        if (type === CouponType.FormCoupon) {
            switch (fixtureFormType) {
                case FixtureFormMode.Away:
                    return GridSorting.AwayForm;
                case FixtureFormMode.Overall:
                    return GridSorting.OverallForm;
                case FixtureFormMode.Home:
                default:
                    return GridSorting.HomeForm;
            }
        }

        return this.sortingStore.get('coupons', sport) || GridSorting.Competition;
    }

    private mapCouponType(coupon: CdsCoupon): CouponType {
        if (coupon.isFormCoupon === true) {
            return CouponType.FormCoupon;
        } else {
            return CouponType.Regular;
        }
    }
}
